import { IconClock } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnline } from '@/hooks';
import { logger } from '@/lib/logger';
import { navigate } from '@/lib/router';
import type { Booking } from '@/types/api';
import { nextBooking } from '@/utils/nextBooking';
import { BOOKINGS_CHANGED, bookingsApi } from './api';

const REFRESH_MS = 60_000;

const clock = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** Today's next booking (of `doctorId`, or anyone's), refreshed every minute and on any booking change. */
function useNextBooking(doctorId: string | null): Booking | null {
  const online = useOnline();
  const [next, setNext] = useState<Booking | null>(null);
  // Only the latest request may set the result: an older one (another doctor,
  // or overtaken by a poll) can answer last.
  const latest = useRef(0);

  const load = useCallback(() => {
    const mine = ++latest.current;
    const now = new Date();
    const endOfDay = new Date(now);
    endOfDay.setHours(24, 0, 0, 0);
    bookingsApi
      .list(now, endOfDay, doctorId)
      .then((list) => {
        if (mine === latest.current) setNext(nextBooking(list, now));
      })
      .catch((err) => logger.warn('next booking failed', err));
  }, [doctorId]);

  useEffect(() => {
    if (!online) return;
    load();
    const timer = window.setInterval(load, REFRESH_MS);
    window.addEventListener(BOOKINGS_CHANGED, load);
    window.addEventListener('focus', load);
    return () => {
      // Answers still on the way belong to the old doctor (or none).
      latest.current += 1;
      window.clearInterval(timer);
      window.removeEventListener(BOOKINGS_CHANGED, load);
      window.removeEventListener('focus', load);
    };
  }, [online, load]);

  return online ? next : null;
}

/** "Next patient: Kiss Anna · 10:30" in the middle of the top bar; opens the chart when linked. */
export function NextPatient({ doctorId }: { doctorId: string | null }) {
  const { t } = useTranslation();
  const next = useNextBooking(doctorId);
  if (!next) return null;
  const patient = next.patient;
  return (
    <button
      type="button"
      className="topbar__next"
      onClick={() => navigate(patient ? { name: 'patient', id: patient.id } : { name: 'patients' })}
    >
      <IconClock size={18} aria-hidden="true" />
      <span className="topbar__next-label">{t('shell.nextPatient')}</span>
      <strong className="topbar__next-name">{next.name}</strong>
      <span className="topbar__next-time">{clock(next.startsAt)}</span>
    </button>
  );
}
